/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, MouseEvent, useEffect, useState} from 'react';
import {ClickAwayListener as MuiClickAwayListener} from '@material-ui/core';

export interface ClickAwayListenerProps {
  children: string | JSX.Element;
  mountListener: boolean;
  onClickAway: (e: MouseEvent<Document>) => void;
  animationDuration?: number;
  // listens for click only, not touchend - touchend fires before the
  // tapped element's own click, so a toggle button tapped to close its own
  // panel would otherwise be closed by this listener then re-opened by
  // the button's click handler...
  disableTouchEvent?: boolean;
}

/**
 * @ClickAwayListener
 *
 * Component that more properly un-mounts the click away listener based on the
 * passed mountListener prop. This is meant to prevent the listener from
 * being perpetually mounted.
 *
 * @param {JSX.Element} children: component children
 * @param {boolean} mountListener: prop that determines if the click away listener
 *   should be used
 * @param {Function} onClickAway: callback fired when clicked outside of child container
 * @param {number} animationDuration: if animation needed preserves that before
 *   mounting/un-mounting the listener in milliseconds
 *
 * ```typescript
 *
 * import {ClickAwayListener} from '@ion/dls/src/components/listeners/ClickAwayListener';
 *
 * <ClickAwayListener mountListener={menuOpen}
 *   onClickAway={handleMenuClose}>
 *   <div>
 *     HELLO
 *   </div>
 * </ClickAwayListener>
 *
 * ```
 *
 */
export const ClickAwayListener = ({
  children,
  mountListener,
  onClickAway,
  animationDuration = 0,
  disableTouchEvent = false
}: ClickAwayListenerProps) => {
  const [mountPropValue, setMountPropValue] = useState(mountListener);
  useEffect(() => {
    setTimeout(() => {
      setMountPropValue(mountListener);
    }, animationDuration);
  }, [mountListener]);

  return (
    mountPropValue ? (
      <MuiClickAwayListener onClickAway={onClickAway}
        {...disableTouchEvent && {touchEvent: false}}>
        <div>{children}</div>
      </MuiClickAwayListener>
    ) : (
      <>
        {children}
      </>
    )
  );
};

export default memo(ClickAwayListener);
